import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  dailyProcessApi,
  ATTENDANCE_STATUS_OPTIONS,
  SEARCH_FIELD_OPTIONS,
  TOOLS_OPTIONS,
  DAILY_ATTENDANCE_PREDEFINED_REPORTS,
} from '../api/dailyProcessApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'empId', label: 'Emp Code' },
  { key: 'name', label: 'Employee Name' },
  { key: 'company', label: 'Company' },
  { key: 'branch', label: 'Branch' },
  { key: 'department', label: 'Department' },
  { key: 'dateDisplay', label: 'Date' },
  { key: 'firstSwipe', label: 'First Swipe' },
  { key: 'lastSwipe', label: 'Last Swipe' },
  { key: 'totalHours', label: 'Total Hours' },
  { key: 'attendance', label: 'Attendance' },
];

export function useDailyProcessList() {
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [companyFilter, setCompanyFilter] = useState(0);
  const [branchFilter, setBranchFilter] = useState(0);
  const [departmentFilter, setDepartmentFilter] = useState(0);
  const [employeeFilter, setEmployeeFilter] = useState(0);
  const [groupFilter, setGroupFilter] = useState(0);
  const [genderFilter, setGenderFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [searchText, setSearchText] = useState('');

  const [isReportsDrawerOpen, setIsReportsDrawerOpen] = useState(false);

  const [options, setOptions] = useState({
    companies: [{ value: 0, label: 'All Companies' }],
    branches: [{ value: 0, label: 'All Branches' }],
    departments: [{ value: 0, label: 'All Departments' }],
    employees: [{ value: 0, label: 'All Employees' }],
    groups: [{ value: 0, label: 'All Groups' }],
    genders: [
      { value: 'All', label: 'All Genders' },
      { value: 'M', label: 'Male' },
      { value: 'F', label: 'Female' },
    ],
  });

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Background Export Job via JobEngine
  const { triggerExport, isExporting } = useExportJob({
    module: 'daily_process',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  // Load dropdown options
  useEffect(() => {
    dailyProcessApi
      .getFilterOptions()
      .then((data) => {
        if (data) {
          setOptions({
            companies: data.companies || [{ value: 0, label: 'All Companies' }],
            branches: data.branches || [{ value: 0, label: 'All Branches' }],
            departments: data.departments || [{ value: 0, label: 'All Departments' }],
            employees: data.employees || [{ value: 0, label: 'All Employees' }],
            groups: data.groups || [{ value: 0, label: 'All Groups' }],
            genders: data.genders || [
              { value: 'All', label: 'All Genders' },
              { value: 'M', label: 'Male' },
              { value: 'F', label: 'Female' },
            ],
          });
        }
      })
      .catch(() => {
        // Fallback silently if options endpoint encounters error
      });
  }, []);

  const activeFilters = useMemo(
    () => ({
      fromDate,
      toDate,
      company: companyFilter,
      branch: branchFilter,
      department: departmentFilter,
      employee: employeeFilter,
      group: groupFilter,
      gender: genderFilter,
      status: statusFilter,
      search: searchText,
    }),
    [fromDate, toDate, companyFilter, branchFilter, departmentFilter, employeeFilter, groupFilter, genderFilter, statusFilter, searchText]
  );

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dailyProcessApi.getRecords({
        ...activeFilters,
        page,
        pageSize,
      });
      setRecords(data.items);
      setTotalCount(data.total);
    } catch (err) {
      appToast.error('Failed to load Daily Attendance records.');
    } finally {
      setIsLoading(false);
    }
  }, [activeFilters, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setCompanyFilter(0);
    setBranchFilter(0);
    setDepartmentFilter(0);
    setEmployeeFilter(0);
    setGroupFilter(0);
    setGenderFilter('All');
    setStatusFilter('All');
    setFromDate(todayStr);
    setToDate(todayStr);
    setSearchText('');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleDelete = async (id, employee) => {
    const confirmed = await appConfirm({
      title: 'Delete Attendance Record',
      message: `Are you sure you want to delete attendance record for ${employee}?`,
      confirmText: 'Delete Record',
      variant: 'danger',
      icon: 'delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await dailyProcessApi.deleteRecord(id);
      appToast.success('Attendance record deleted successfully.');
      loadRecords();
    } catch (err) {
      appToast.error('Failed to delete attendance record.');
    }
  };

  const handleReportAction = async (actionKey, reportKey) => {
    if (actionKey === 'print') {
      let repCols = REPORT_COLUMNS;
      let repTitle = 'Daily Attendance Report';
      if (reportKey === 'daywise_attendance_matrix') {
        repTitle = 'Day-wise Attendance Report';
        repCols = [
          { key: 'emp_code', label: 'Emp Code' },
          { key: 'emp_name', label: 'Employee Name' },
          { key: 'company_name', label: 'Company' },
          { key: 'branch_name', label: 'Branch' },
          { key: 'department_name', label: 'Department' },
          ...Array.from({ length: 31 }, (_, i) => ({
            key: `day_${i + 1}`,
            label: `${i + 1}`,
            render: (row) => row.days?.[i + 1] ?? '-',
          })),
          { key: 'total_present', label: 'Present' },
          { key: 'total_absent', label: 'Absent' },
          { key: 'total_late', label: 'Late' },
          { key: 'total_half_day', label: 'Half Day' },
        ];
      } else if (reportKey === 'daily_overtime_report') {
        repTitle = 'Daily Overtime Report';
        repCols = [
          { key: 'emp_code', label: 'Emp Code' },
          { key: 'emp_name', label: 'Employee Name' },
          { key: 'company_name', label: 'Company' },
          { key: 'branch_name', label: 'Branch' },
          { key: 'department_name', label: 'Department' },
          { key: 'ADate', label: 'Date' },
          { key: 'shift_intime', label: 'Shift In' },
          { key: 'shift_outtime', label: 'Shift Out' },
          { key: 'FirstSwipe', label: 'First Swipe' },
          { key: 'LastSwipe', label: 'Last Swipe' },
          { key: 'overtime_display', label: 'Overtime' },
        ];
      }

      // Apply customized column settings if saved from ReportColumnsModal
      const savedColsJson = localStorage.getItem(`report_cols_${reportKey}`);
      if (savedColsJson) {
        try {
          const savedCols = JSON.parse(savedColsJson);
          const visibleSaved = savedCols.filter((c) => c.visible);
          if (visibleSaved.length > 0) {
            const reorderedCols = [];
            visibleSaved.forEach((sc) => {
              const matched = repCols.find(
                (rc) => rc.label === sc.label || rc.label === sc.key || rc.key === sc.key
              );
              if (matched) {
                reorderedCols.push(matched);
              }
            });
            if (reorderedCols.length > 0) {
              repCols = reorderedCols;
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      executePrintReport({
        title: `${repTitle} (${fromDate} to ${toDate})`,
        columns: repCols,
        metadata: {
          'Date Range': `${fromDate} to ${toDate}`,
          'Report': repTitle,
          'Status': statusFilter || 'All',
        },
        fetchReportData: async () => {
          const res = await dailyProcessApi.fetchReportData(reportKey, activeFilters);
          return { items: res.items || [] };
        },
        onExportPdf: () => triggerExport({ ...activeFilters, report_key: reportKey }, 'PDF'),
        onExportExcel: () => triggerExport({ ...activeFilters, report_key: reportKey }, 'XLSX'),
        threshold: 2000,
      });
    } else if (actionKey === 'excel') {
      await triggerExport({ ...activeFilters, report_key: reportKey }, 'XLSX');
    } else if (actionKey === 'pdf') {
      await triggerExport({ ...activeFilters, report_key: reportKey }, 'PDF');
    }
  };

  const handleToolSelect = async (toolKey) => {
    if (toolKey === 'print' || toolKey === 'print-report') {
      setIsReportsDrawerOpen(true);
    } else if (toolKey === 'export_excel') {
      await triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      await triggerExport(activeFilters, 'PDF');
    } else if (toolKey === 'rollback') {
      const confirmed = await appConfirm({
        title: 'Rollback Daily Attendance',
        message: `Are you sure you want to rollback daily attendance for the period ${fromDate} to ${toDate}? This will clear processed attendance logs for the selected criteria.`,
        confirmText: 'Rollback Logs',
        variant: 'danger',
        icon: 'history',
      });
      if (!confirmed) {
        return;
      }
      try {
        const res = await dailyProcessApi.rollbackDailyAttendance(activeFilters);
        appToast.success(res.message || 'Daily attendance logs rolled back successfully.');
        loadRecords();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        appToast.error(detail || 'Failed to rollback daily attendance.');
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return {
    state: {
      records,
      isLoading,
      isExporting,
      companyFilter,
      branchFilter,
      departmentFilter,
      employeeFilter,
      groupFilter,
      genderFilter,
      statusFilter,
      fromDate,
      toDate,
      searchText,
      isReportsDrawerOpen,
      page,
      pageSize,
      totalCount,
      totalPages,
      companyOptions: options.companies,
      branchOptions: options.branches,
      departmentOptions: options.departments,
      employeeOptions: options.employees,
      groupOptions: options.groups,
      genderOptions: options.genders,
      attendanceStatusOptions: ATTENDANCE_STATUS_OPTIONS,
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      tools: TOOLS_OPTIONS,
      predefinedReports: DAILY_ATTENDANCE_PREDEFINED_REPORTS,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setEmployeeFilter,
      setGroupFilter,
      setGenderFilter,
      setStatusFilter,
      setFromDate,
      setToDate,
      setSearchText,
      setIsReportsDrawerOpen,
      handleReportAction,
      handleStatusTabChange,
      setPage,
      handleSearch,
      resetFilters,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
